/**
 * Utilidades de normalización de teléfonos para llamadas Telnyx.
 * Telnyx requiere formato E.164 (ej: +34600123456).
 *
 * En el CRM no hay validación de formato, así que los teléfonos pueden venir como:
 *   "+34600123456"  → ya correcto
 *   "600123456"     → falta prefijo país
 *   "+34 600 123 456" → con espacios
 *   "34600123456"   → sin +
 *   "0034600123456" → prefijo internacional con 00
 *   "600 12 34 56"  → con espacios sin prefijo
 *   "(+34) 600123456" → con paréntesis
 */

/**
 * Normaliza un número de teléfono a formato E.164 para Telnyx.
 *
 * Reglas:
 * 1. Limpia espacios, guiones, paréntesis, puntos
 * 2. Si empieza con +, lo deja (ya tiene prefijo)
 * 3. Si empieza con 00, reemplaza 00 por +
 * 4. Si empieza con 34 y tiene 11+ dígitos, antepone +
 * 5. Si es un número español de 9 dígitos (6xx, 7xx, 8xx, 9xx), antepone +34
 * 6. Otros: antepone + como fallback
 */
export function normalizePhoneE164(raw: string): string {
  // 1. Limpiar caracteres no numéricos excepto + al inicio
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Extraer signo + si está al inicio, luego limpiar todo lo demás
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  if (!digits) return trimmed; // nada que normalizar

  // 2. Ya tiene + al inicio → correcto, devolver +digits
  if (hasPlus) {
    return `+${digits}`;
  }

  // 3. Empieza con 00 (formato internacional europeo) → reemplazar 00 por +
  if (digits.startsWith('00') && digits.length > 4) {
    return `+${digits.slice(2)}`;
  }

  // 4. Empieza con 34 y tiene 11+ dígitos → es +34 sin el +
  if (digits.startsWith('34') && digits.length >= 11) {
    return `+${digits}`;
  }

  // 5. Número español de 9 dígitos (móvil 6xx/7xx, fijo 8xx/9xx)
  if (digits.length === 9 && /^[6-9]/.test(digits)) {
    return `+34${digits}`;
  }

  // 6. Fallback: anteponer + y dejar que Telnyx decida
  return `+${digits}`;
}

/**
 * Convierte un teléfono al formato que `wa.me` espera: solo dígitos (sin `+`).
 *
 * Devuelve `null` si el input es vacío, sin dígitos, o queda fuera de
 * E.164 (7–15 dígitos).
 *
 * Ejemplos:
 *   "+34 614 89 34 74" → "34614893474"
 *   "(+34) 614-81-35-68" → "34614813568"
 *   "" / null / "abc" → null
 *   "+0" → null (demasiado corto)
 */
export function phoneToWaDigits(input: string | null | undefined): string | null {
  if (!input) return null;
  const digits = String(input).replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  return digits;
}
